/**
 * The guest demo's REST surface, client side (AD-794; design `docs/design/guest-demo.md` §2.2,
 * §6.5, §7.1; story `q-guest-demo-s5-client-guest-mode-and-end-card`).
 *
 * Every call is same-origin by default (the `try.` host serves the SPA, `/api` and `/ws` — design
 * §2.6, no CORS). The guest token NEVER touches `localStorage` or a cookie: it is returned to the
 * caller, which holds it in page memory only (design §2.2 — a guest token can never be mistaken
 * for a real session on the main origin).
 *
 * Shapes: `start`/`end` are S1's (`backend/src/gateway/httpApi.ts` `guestStart`/`guestEnd`);
 * `config`/`out` are this story's; `queueJoin`/`queueHeartbeat` and the start body
 * `{ turnstileToken } | { claimTicket }` are S3's (`q-guest-demo-s3-abuse-controls-queue-and-
 * observability`) — coded to the design §7.1 shapes and the S3 branch's handlers. Until S3 is
 * deployed a guest process answers the queue routes with a 404, which the flow treats as "the
 * queue is not available yet" (`guestFlow.ts`), never as a crash.
 */

import { serverHttpBase } from '../net/platform.js';

/** The closed outbound-link enum (design §6.5); mirrors `backend/src/config/guest-launch.ts`. */
export const GUEST_CTA_TARGETS = [
  'newsletter',
  'register',
  'devlog',
  'website',
  'social_discord',
  'social_x',
  'social_telegram',
  'social_tiktok',
  'social_instagram',
  'social_youtube',
  // AD-810 item 5: optional — the server lists it in `ctaTargets` only once its Brevo form is
  // configured (`GC_GUEST_CTA_URLS`), so the client never shows it otherwise.
  'founder_interest',
] as const;

export type GuestCtaTarget = (typeof GUEST_CTA_TARGETS)[number];

/**
 * What the client assumes when the server's config omits `ctaTargets` (an older guest process):
 * every target that has a seed destination server-side — never an optional one, which may be
 * unconfigured and would then be a button pointing at a 404.
 */
export const DEFAULT_GUEST_CTA_TARGETS: readonly GuestCtaTarget[] = GUEST_CTA_TARGETS.filter(
  (t) => t !== 'founder_interest',
);

/** `GET /api/guest/config`. */
export interface GuestConfig {
  registrationOpen: boolean;
  ctaTargets: GuestCtaTarget[];
  /** The quest whose turn-in shows the end card (design §5.3). */
  finalQuestId: string | null;
  sessionTtlMs: number;
  queueHeartbeatMs: number;
}

/** `POST /api/guest/start` → 201. */
export interface GuestSession {
  token: string;
  characterId: number;
  /** ISO time the session (and the private instance) ends — fixed, never slid. */
  expiresAt: string;
}

/** `POST /api/guest/queue` → 201. */
export interface GuestQueueEntry {
  queueId: string;
  position: number;
  etaSeconds: number | null;
  expiresAt: string;
}

/** `POST /api/guest/queue/heartbeat` → 200. */
export type GuestQueueBeat =
  | { status: 'waiting'; position: number; etaSeconds: number | null; expiresAt: string }
  | { status: 'claim'; claimTicket: string; claimExpiresAt: string };

/** A refused guest request: the server's error code (or `network`) and HTTP status (0 = none). */
export class GuestApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'GuestApiError';
  }
}

/** The transport the flow drives — injectable so the state machine is testable without a server. */
export interface GuestApi {
  config(): Promise<GuestConfig>;
  start(body: { turnstileToken?: string } | { claimTicket: string }): Promise<GuestSession>;
  end(token: string): Promise<void>;
  queueJoin(body: { turnstileToken?: string }): Promise<GuestQueueEntry>;
  queueHeartbeat(queueId: string): Promise<GuestQueueBeat>;
}

/** The counted redirect every outbound demo link goes through (design §6.5): never a raw URL. */
export function guestOutHref(
  target: GuestCtaTarget,
  base: string = serverHttpBase(),
  from?: GuestCtaSource,
): string {
  return `${base}/api/guest/out/${target}${from ? `?from=${from}` : ''}`;
}

/**
 * Where a CTA click came from, when it is not the end card or the queue page (AD-833): the death
 * card's links carry `?from=death`, so the server counts them apart (`guest_cta_click_from_death`)
 * — a bounded counter with no identifier. The destination never sees it.
 */
export type GuestCtaSource = 'death';

/** Narrow the server's config body defensively (an older guest process may omit fields). */
export function parseGuestConfig(body: unknown): GuestConfig {
  const rec = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const known = new Set<string>(GUEST_CTA_TARGETS);
  const targets = Array.isArray(rec['ctaTargets'])
    ? (rec['ctaTargets'] as unknown[]).filter(
        (t): t is GuestCtaTarget => typeof t === 'string' && known.has(t),
      )
    : [...DEFAULT_GUEST_CTA_TARGETS];
  const num = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
  return {
    // Fail toward the launch mode: anything but an explicit `true` keeps the newsletter leading.
    registrationOpen: rec['registrationOpen'] === true,
    ctaTargets: targets,
    finalQuestId: typeof rec['finalQuestId'] === 'string' ? rec['finalQuestId'] : null,
    sessionTtlMs: num(rec['sessionTtlMs'], 1_200_000),
    queueHeartbeatMs: num(rec['queueHeartbeatMs'], 15_000),
  };
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** The real transport over `fetch`. `fetchImpl`/`base` are injectable for tests. */
export function createGuestApi(
  fetchImpl: Fetch = (input, init) => fetch(input, init),
  base: () => string = serverHttpBase,
): GuestApi {
  async function call<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
    let res: Response;
    try {
      res = await fetchImpl(`${base()}${path}`, {
        method,
        ...init,
        headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
        // Same-origin bearer only: no cookie is sent or set (design §2.6).
        credentials: 'omit',
      });
    } catch (error) {
      throw new GuestApiError('network', 0, error instanceof Error ? error.message : String(error));
    }
    if (res.status === 204) return undefined as T;
    const body: unknown = await res.json().catch(() => ({}));
    if (!res.ok) {
      const e =
        typeof body === 'object' && body !== null && 'error' in body
          ? (body as { error: { code?: string; message?: string } }).error
          : {};
      throw new GuestApiError(
        e.code ?? 'unknown',
        res.status,
        e.message ?? `request failed (${res.status})`, // id-ok: HTTP status number
      );
    }
    return body as T;
  }
  return {
    config: async () => parseGuestConfig(await call<unknown>('GET', '/api/guest/config')),
    start: (body) => call<GuestSession>('POST', '/api/guest/start', { body: JSON.stringify(body) }),
    end: (token) =>
      call<void>('POST', '/api/guest/end', { headers: { authorization: `Bearer ${token}` } }),
    queueJoin: (body) =>
      call<GuestQueueEntry>('POST', '/api/guest/queue', { body: JSON.stringify(body) }),
    queueHeartbeat: (queueId) =>
      call<GuestQueueBeat>('POST', '/api/guest/queue/heartbeat', {
        body: JSON.stringify({ queueId }),
      }),
  };
}
